import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  CreditCard, 
  Plus, 
  Minus, 
  ShoppingCart, 
  Check,
  AlertCircle,
  Calendar,
  Loader2,
  ArrowLeft,
  Zap,
  Clock,
  Info,
  Upload,
  FileText,
  Image as ImageIcon,
  X,
  Crown,
  CreditCard as CardIcon,
  Palette,
  Globe,
  Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const colorOptions = {
  white: { label: 'Blanc', color: 'bg-white border-2 border-slate-300', textColor: 'text-slate-900' },
  black: { label: 'Noir', color: 'bg-slate-900 border-2 border-slate-900', textColor: 'text-white' },
  blue: { label: 'Bleu', color: 'bg-blue-600 border-2 border-blue-600', textColor: 'text-white' }
};

const productIcons = {
  brand: CardIcon,
  custom: Palette,
  haiti_mondial: Globe
};

export default function BuyMoreCards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('white');
  const [paymentMode, setPaymentMode] = useState('stripe');
  const [calculation, setCalculation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Design file upload states
  const [designFile, setDesignFile] = useState(null);
  const [designFileId, setDesignFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Coupon states
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);

  useEffect(() => {
    fetchBuyCardsInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      calculatePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, quantity, selectedColor, designFileId, couponCode, paymentMode]);

  const fetchBuyCardsInfo = async () => {
    try {
      const response = await fetch(`${API}/owner/buy-cards/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInfo(data);
        if (data.compatible_products?.length > 0) {
          setSelectedProduct(data.compatible_products[0]);
        }
      } else {
        toast.error(t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!selectedProduct) return;
    
    // Skip calculation for custom without design file
    const productKey = selectedProduct.key || selectedProduct.product_id?.replace('card_', '');
    if (productKey === 'custom' && !designFileId) {
      setCalculation(null);
      return;
    }
    
    try {
      const response = await fetch(`${API}/owner/buy-cards/calculate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_type: productKey,
          quantity: quantity,
          color: productKey === 'brand' ? selectedColor : null,
          design_file_id: productKey === 'custom' ? designFileId : null,
          coupon_code: couponCode && paymentMode === 'stripe' ? couponCode : null,
          payment_mode: paymentMode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalculation(data);
        
        // Update coupon status
        if (data.coupon) {
          if (data.coupon.valid === false) {
            setCouponApplied({ valid: false, message: data.coupon.message });
          } else if (data.coupon.code) {
            setCouponApplied({ valid: true, ...data.coupon });
          } else {
            setCouponApplied(null);
          }
        } else {
          setCouponApplied(null);
        }
      }
    } catch (error) {
      console.error('Failed to calculate:', error);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    const maxQty = info?.remaining_slots || 100;
    if (newQty >= 1 && newQty <= maxQty) {
      setQuantity(newQty);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Formats acceptés: PDF, PNG, JPG');
      return;
    }
    
    // Validate file size (10 MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Taille max: 10 MB');
      return;
    }
    
    setDesignFile(file);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/owner/upload-design`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setDesignFileId(data.file_id);
        toast.success('Design uploadé avec succès');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'upload');
        setDesignFile(null);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      setDesignFile(null);
    } finally {
      setUploading(false);
    }
  };

  const removeDesignFile = () => {
    setDesignFile(null);
    setDesignFileId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedProduct || !calculation) return;
    
    const productKey = selectedProduct.key || selectedProduct.product_id?.replace('card_', '');
    
    // Validate required fields
    if (productKey === 'brand' && !selectedColor) {
      toast.error('Veuillez sélectionner une couleur');
      return;
    }
    
    if (productKey === 'custom' && !designFileId) {
      toast.error('Veuillez uploader un fichier design');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/owner/buy-cards/order`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_type: productKey,
          quantity: quantity,
          color: productKey === 'brand' ? selectedColor : null,
          payment_mode: paymentMode,
          design_file_id: productKey === 'custom' ? designFileId : null,
          coupon_code: couponApplied?.valid && paymentMode === 'stripe' ? couponCode : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (paymentMode === 'stripe') {
          // Create Stripe session
          try {
            const stripeResponse = await fetch(`${API}/checkout/stripe/session`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                order_id: data.order_id,
                origin_url: window.location.origin
              })
            });
            
            if (stripeResponse.ok) {
              const stripeData = await stripeResponse.json();
              window.location.href = stripeData.checkout_url;
              return;
            }
          } catch (stripeError) {
            console.warn('Stripe error:', stripeError);
          }
        }
        
        // Manual payment - show success message
        toast.success(data.message);
        navigate('/owner', { state: { newOrder: data.order_id } });
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.serverError'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0A2540] animate-spin" />
      </div>
    );
  }

  // Check if user can add more cards
  if (info && !info.can_add_more) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-soft p-8 text-center"
          >
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">
              {t('buyCards.limitReached')}
            </h1>
            <p className="text-slate-600 mb-6">
              {info.upgrade_message}
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-500">{t('buyCards.currentPlan')}</p>
              <p className="font-semibold text-slate-900">{info.account_type?.toUpperCase()}</p>
              <p className="text-sm text-slate-500 mt-2">{t('buyCards.cardsUsed')}: {info.current_cards} / {info.plan_limit}</p>
            </div>
            <Button
              onClick={() => navigate('/owner')}
              className="btn-primary rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('buyCards.backToDashboard')}
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const productKey = selectedProduct?.key || selectedProduct?.product_id?.replace('card_', '');

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB]">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/owner')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {t('buyCards.title')}
            </h1>
            <p className="text-slate-600">{t('buyCards.subtitle')}</p>
          </div>
        </div>

        {/* Plan Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl p-5 text-white mb-8"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white/80 text-sm">{t('buyCards.currentPlan')}</p>
                <p className="font-bold text-xl">{info?.account_type?.toUpperCase()}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-white/80 text-sm">{t('buyCards.cardsUsed')}</p>
                <p className="font-bold text-lg">{info?.current_cards} / {info?.plan_limit}</p>
              </div>
              {info?.subscription_expires && (
                <div className="text-right">
                  <p className="text-white/80 text-sm">{t('buyCards.expiresOn')}</p>
                  <p className="font-bold text-lg">
                    {new Date(info.subscription_expires).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Product Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-soft p-6"
            >
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {t('buyCards.selectCard')}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                {info?.compatible_products?.map((product) => {
                  const key = product.key || product.product_id?.replace('card_', '');
                  const isSelected = productKey === key;
                  const ProductIcon = productIcons[key] || CreditCard;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedProduct(product);
                        setQuantity(1);
                        // Reset design file when switching products
                        if (key !== 'custom') {
                          removeDesignFile();
                        }
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-[#0A2540]/5' 
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                      data-testid={`product-${key}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[#0A2540]/50 text-white' : 'bg-slate-100'
                        }`}>
                          <ProductIcon className="w-5 h-5" />
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-blue-500 ml-auto" />
                        )}
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{product.name}</p>
                      <p className="text-lg font-bold text-[#0A2540] mt-1">
                        ${product.total_per_card.toFixed(2)}
                        <span className="text-xs text-slate-500 font-normal">/carte</span>
                      </p>
                      {product.pricing_rule === 'fixed_8' && (
                        <span className="inline-block mt-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Prix fixe ≤4 mois
                        </span>
                      )}
                      {product.pricing_rule === 'fixed_15_free_plan' && (
                        <span className="inline-block mt-2 px-2 py-1 bg-[#00C48C]/10 text-[#00A67E] text-xs rounded-full">
                          Plan gratuit inclus
                        </span>
                      )}
                      {product.pricing_rule === 'card_only_first_year_free' && (
                        <span className="inline-block mt-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          1ère année offerte
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>

            {/* Color Selection (Brand Card Only) */}
            <AnimatePresence>
              {productKey === 'brand' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-white rounded-2xl shadow-soft p-6"
                >
                  <h3 className="font-semibold text-slate-900 mb-4">Couleur de la carte *</h3>
                  <div className="flex gap-3">
                    {Object.entries(colorOptions).map(([key, opt]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedColor(key)}
                        className={`w-16 h-16 rounded-xl ${opt.color} flex items-center justify-center transition-transform ${
                          selectedColor === key ? 'ring-2 ring-[#3B82F6] ring-offset-2 scale-110' : ''
                        }`}
                        data-testid={`color-${key}`}
                      >
                        {selectedColor === key && (
                          <Check className={`w-6 h-6 ${opt.textColor}`} />
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-3">
                    Couleur sélectionnée: <span className="font-medium">{colorOptions[selectedColor]?.label}</span>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Design Upload (Custom Design Only) */}
            <AnimatePresence>
              {productKey === 'custom' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-white rounded-2xl shadow-soft p-6"
                >
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Upload du design *
                  </h3>
                  
                  {!designFile ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-[#0A2540]/5 transition-colors"
                    >
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="font-medium text-slate-700">Cliquez pour uploader votre design</p>
                      <p className="text-sm text-slate-500 mt-2">
                        PDF (recommandé), PNG ou JPG • Max 10 MB
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Résolution recommandée: 300 DPI • Format: 86 × 54 mm
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-[#00C48C]/5 border border-[#00C48C]/20 rounded-xl">
                      <div className="flex items-center gap-3">
                        {designFile.type === 'application/pdf' ? (
                          <FileText className="w-8 h-8 text-red-500" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-blue-500" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{designFile.name}</p>
                          <p className="text-sm text-slate-500">
                            {(designFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {uploading ? (
                          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                        ) : designFileId ? (
                          <Check className="w-5 h-5 text-green-500" />
                        ) : null}
                        <button
                          onClick={removeDesignFile}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Le même design sera appliqué à toutes les cartes de la commande
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quantity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-soft p-6"
            >
              <h3 className="font-semibold text-slate-900 mb-4">Quantité</h3>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                  className="rounded-full w-10 h-10 p-0"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    if (val >= 1 && val <= (info?.remaining_slots || 100)) {
                      setQuantity(val);
                    }
                  }}
                  className="w-24 text-center text-lg font-bold"
                  min={1}
                  max={info?.remaining_slots || 100}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= (info?.remaining_slots || 100)}
                  className="rounded-full w-10 h-10 p-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500">
                  (max: {info?.remaining_slots || 100} cartes)
                </span>
              </div>
            </motion.div>

            {/* Coupon Code (Stripe payment only) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-2xl shadow-soft p-6"
            >
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Code promo
              </h3>
              
              <div className="flex gap-3">
                <Input
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    if (!e.target.value) {
                      setCouponApplied(null);
                    }
                  }}
                  placeholder="Entrez votre code promo"
                  className="flex-1 uppercase"
                  disabled={paymentMode === 'manual'}
                  data-testid="coupon-input"
                />
              </div>
              
              {/* Coupon Status */}
              {couponApplied && (
                <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                  couponApplied.valid 
                    ? 'bg-[#00C48C]/5 border border-[#00C48C]/20 text-[#00A67E]' 
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {couponApplied.valid ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span className="text-sm">{couponApplied.message}</span>
                </div>
              )}
              
              {paymentMode === 'manual' && (
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Les coupons ne sont pas disponibles pour le paiement manuel
                </p>
              )}
            </motion.div>

            {/* Payment Mode */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-soft p-6"
            >
              <h3 className="font-semibold text-slate-900 mb-4">Mode de paiement *</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPaymentMode('stripe')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    paymentMode === 'stripe'
                      ? 'border-blue-500 bg-[#0A2540]/5'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                  data-testid="payment-stripe"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold">Paiement automatique</span>
                    {paymentMode === 'stripe' && <Check className="w-4 h-4 text-blue-500 ml-auto" />}
                  </div>
                  <p className="text-sm text-slate-500">
                    Paiement immédiat par carte bancaire via Stripe
                  </p>
                </button>
                
                <button
                  onClick={() => setPaymentMode('manual')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    paymentMode === 'manual'
                      ? 'border-blue-500 bg-[#0A2540]/5'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                  data-testid="payment-manual"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold">Paiement manuel</span>
                    {paymentMode === 'manual' && <Check className="w-4 h-4 text-blue-500 ml-auto" />}
                  </div>
                  <p className="text-sm text-slate-500">
                    Virement bancaire ou autre. Validation par l'admin requise.
                  </p>
                </button>
              </div>
              
              {paymentMode === 'manual' && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Les profils seront créés mais resteront <strong>cachés</strong> jusqu'à validation de votre paiement par l'admin.
                  </span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-soft p-6 sticky top-24"
            >
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Résumé de commande
              </h2>

              {calculation ? (
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{calculation.product}</span>
                    <span>× {calculation.quantity}</span>
                  </div>
                  
                  {calculation.card_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{t('buyCards.cards')}</span>
                      <span>${calculation.card_total.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {calculation.subscription_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">
                        {t('buyCards.subscription')} ({calculation.subscription_months} {t('buyCards.months')})
                      </span>
                      <span>${calculation.subscription_total.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {calculation.first_year_free && (
                    <div className="flex justify-between text-sm text-[#00C48C]">
                      <span>🎁 {t('buyCards.subscriptionFree')}</span>
                      <span>$0.00</span>
                    </div>
                  )}
                  
                  {calculation.pricing_rule === 'fixed_8' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <p className="font-medium text-amber-700">Prix fixe appliqué</p>
                      <p className="text-amber-600 text-xs mt-1">
                        8$/carte (moins de 4 mois restants sur l'abonnement)
                      </p>
                    </div>
                  )}
                  
                  {/* Coupon Discount */}
                  {calculation.discount_amount > 0 && calculation.coupon && (
                    <div className="bg-[#00C48C]/5 border border-[#00C48C]/20 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-[#00C48C]" />
                          <span className="font-medium text-[#00A67E]">
                            Coupon {calculation.coupon.code}
                          </span>
                        </div>
                        <span className="font-bold text-[#00C48C]">
                          -{calculation.discount_amount.toFixed(2)}$
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="border-t border-slate-200 pt-4">
                    {calculation.discount_amount > 0 && (
                      <div className="flex justify-between text-sm text-slate-500 line-through mb-1">
                        <span>Sous-total</span>
                        <span>${calculation.total.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-[#0A2540]">
                        ${(calculation.final_total || calculation.total).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>Expiration: {new Date(calculation.subscription_expires).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <p className="mt-1">{calculation.message}</p>
                  </div>
                  
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || (productKey === 'custom' && !designFileId)}
                    className="w-full btn-primary h-12 rounded-full text-lg"
                    data-testid="submit-order-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Traitement...
                      </>
                    ) : paymentMode === 'stripe' ? (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        Pay now
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Submit Order
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  {productKey === 'custom' && !designFileId ? (
                    <p>Veuillez uploader votre design pour voir le prix</p>
                  ) : (
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
